<template>
  <div class="container">
    <div class="container__headline">
      <h1 class="h1 h1--underline">
        {{ $tv('hydraulicSelectorTitle', 'hydraulicSelector') }}
      </h1>
    </div>
    <div class="container__content">
      <HydraulicSelector />
    </div>
    <div class="disclaimer-message">
      <span v-tv:hydraulicSelector="'disclaimerMessageScreenSize'" />
    </div>
    <PopupHydraSDisclaimer
      :show="showDisclaimer"
      :class="showDisclaimer ? 'popup--visible' : ''"
    />
  </div>
</template>

<script setup lang="ts">
import {useHydraulicSelectorStore} from '~/store/hydraulic-selector'
import HydraulicSelector from '~/components/hydraulic-selector/HydraulicSelector'
import useBreadcrumb from '~/composables/useBreadcrumb'
import useI18nExtended from '~/composables/usei18nExtended'
import PopupHydraSDisclaimer from '~/components/popup/PopupHydraSDisclaimer.vue'
import {useConfigurationDataStore} from '@/store/configuration-data'
import createSeoMetaInfo from '~/assets/js/opengraph/helper.ts'
import {useCanonicalPathHelper} from '~/composables/canonical_path_helper'

const i18n = useI18nExtended()
const {adjustCanonicalPath} = useCanonicalPathHelper()

onMounted(() => {
  useBreadcrumb([
    {
      label: i18n.tv('hydraulicSelector', 'breadcrumbs'),
      path: '/hydraulic-selector',
      onclick: () => (useNuxtApp() as any).$eventbus.emit('resetHydraulicData')
    }
  ])
})

const route = useRoute()
const router = useRouter()
let i18nHead = useLocaleHead({addSeoAttributes: true, route: route, router: router, i18n: i18n}).value
adjustCanonicalPath(route.path, i18nHead)
const title = i18n.tv('pageTitle', 'hydraulicSelector')
let description = i18n.tv('hydraulisSelectorDescription', 'seo')

const head = {
  title: title,
  categoryLanguage: i18n.locale,
  htmlAttrs: {
    ...i18nHead.htmlAttrs
  },
  meta: [
    ...i18nHead.meta,
    {
      hid: 'description',
      name: 'description',
      content: description
    }
  ],
  link: [...i18nHead.link]
}
useHead(head)

const BASE_URL = useRuntimeConfig().public.BASE_URL
const region = useNuxtApp().$globalization.getRegion()

const seoInfo = createSeoMetaInfo({
  title: i18n.tv('ogTitle', 'hydraulicSelector'),
  description: i18n.tv('ogDescription', 'hydraulicSelector'),
  image: BASE_URL + '/img/FST_Asset_Zylinderspezifikationen_default.png',
  url: `${BASE_URL}/${region.toLowerCase()}/${i18n.locale.value}/hydraulic-selector`,
  locale: i18n.locale.value
})

useSeoMeta(seoInfo)

const showDisclaimer = computed(() => {
  return !useConfigurationDataStore().acceptedHydraulicSelectorDisclaimer
})

onMounted(() => {
  useHydraulicSelectorStore().replacementStep = 0
})

</script>

<style lang="scss">
.container {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;

  &__headline {
    padding: .5rem 1.625rem 0;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: .625rem;
    align-self: stretch;
    background-color: $color__content--background__primary;

    h1 {
      line-height: 1.95rem;
      margin: 0;
    }
  }

  &__content {
    display: flex;
    align-self: stretch;
    background-color: $color__content--background__primary;
    padding: 1.25rem 1.625rem 0;
  }

  .disclaimer-message {
    display: none;
  }

  @include breakpoint-down($xl) {
    .container__content {
      padding: 1.25rem .25rem 0;
    }
  }

  @include breakpoint-down($hydraulicDesktop) {
    .container__content {
      padding: 1.25rem 1.5rem 0;
      overflow: auto;

      .cylinder-specification-container {
        width: 75rem;
      }
    }
  }

  @include breakpoint-down($hydraulicDisclaimer) {
    .container__content {
      display: none;
    }

    .disclaimer-message {
      display: block;
      font-size: 1.25rem;
      font-weight: $base-text-font-weight-bold;
      margin: 2rem;
      text-align: left;
    }
  }
}
</style>
